// /pages/FleetManagement/Drivers/components/DriverForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    VStack,
    HStack,
    Text,
    Grid,
    CheckboxGroup,
    Checkbox,
    Stack,
    Spinner,
    useColorModeValue,
    FormHelperText,
} from '@chakra-ui/react';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';

interface DriverFormInputs {
    first_name: string;
    last_name: string;
    contact_number: string;
    assigned_truck_plate_numbers: string[];
    is_active: number;
}

interface DriverFormProps {
    driverId?: number;
    initialData?: any;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export function DriverForm({ driverId, initialData, onSuccess, onCancel }: DriverFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [trucks, setTrucks] = useState<{ id: number, plate_number: string }[]>([]);
    const [loadingTrucks, setLoadingTrucks] = useState(true);
    const { showError, showSuccess } = useToastNotification();
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    const isEditMode = !!driverId;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch
    } = useForm<DriverFormInputs>({
        defaultValues: {
            first_name: '',
            last_name: '',
            contact_number: '',
            assigned_truck_plate_numbers: [],
            is_active: 1,
        },
    });

    const selectedPlates = watch('assigned_truck_plate_numbers');

    // Fetch trucks logic
    useEffect(() => {
        const fetchTrucks = async () => {
            try {
                const res = await apiClient.get('/trucks/truck-list');
                setTrucks(res.data.data || res.data);
            } catch (err) {
                console.error("Failed to fetch trucks", err);
            } finally {
                setLoadingTrucks(false);
            }
        };
        fetchTrucks();
    }, []);

    // Reset form when initialData is loaded
    useEffect(() => {
        if (initialData) {
            reset({
                first_name: initialData.first_name,
                last_name: initialData.last_name,
                contact_number: initialData.contact_number,
                assigned_truck_plate_numbers: initialData.assigned_truck_plate_numbers || [],
                is_active: initialData.is_active ?? 1,
            });
        }
    }, [initialData, reset]);

    const onSubmit: SubmitHandler<DriverFormInputs> = async (data) => {
        setIsSubmitting(true);
        try {
            let endpoint: string;
            let successMessage: string;

            if (isEditMode) {
                endpoint = `/drivers/${driverId}`;
                await apiClient.put(endpoint, data);
                successMessage = `Driver ${data.first_name} updated successfully.`;
            } else {
                endpoint = '/drivers';
                await apiClient.post(endpoint, data);
                successMessage = `Driver ${data.first_name} created successfully.`;
            }

            showSuccess('Success', successMessage);
            onSuccess?.();
        } catch (error: any) {
            showError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="700px">
            <VStack spacing={4} align="stretch">
                
                {/* First Name */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel pt={2} fontWeight="medium">
                        First Name <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.first_name}>
                        <Input 
                            {...register('first_name', { required: 'First name is required' })} 
                            placeholder="Enter first name" 
                        />
                        <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Last Name */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel pt={2} fontWeight="medium">
                        Last Name <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.last_name}>
                        <Input 
                            {...register('last_name', { required: 'Last name is required' })} 
                            placeholder="Enter last name" 
                        />
                        <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Contact Number */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel pt={2} fontWeight="medium">
                        Contact No. <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.contact_number}>
                        <Input 
                            {...register('contact_number', { required: 'Contact number is required' })} 
                            placeholder="09XXXXXXXXX" 
                        />
                        <FormErrorMessage>{errors.contact_number?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Assign Trucks */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                    <FormLabel pt={2} fontWeight="medium">Assign Trucks</FormLabel>
                    <FormControl>
                        <Box 
                            p={4} 
                            border="1px solid" 
                            borderColor={borderColor} 
                            borderRadius="md"
                            maxH="180px" 
                            overflowY="auto"
                            bg="gray.50"
                        >
                            {loadingTrucks ? (
                                <HStack spacing={2}><Spinner size="xs" /><Text fontSize="sm">Loading trucks...</Text></HStack>
                            ) : (
                                <CheckboxGroup 
                                    colorScheme="blue" 
                                    value={selectedPlates} 
                                    onChange={(vals) => setValue('assigned_truck_plate_numbers', vals as string[])}
                                >
                                    <Stack spacing={[1, 4]} direction={['column', 'row']} wrap="wrap">
                                        {trucks.map((truck) => (
                                            <Checkbox key={truck.id} value={truck.plate_number}>
                                                <Text fontSize="sm">{truck.plate_number}</Text>
                                            </Checkbox>
                                        ))}
                                    </Stack>
                                </CheckboxGroup>
                            )}
                            {trucks.length === 0 && !loadingTrucks && <Text fontSize="xs" color="gray.500">No trucks available.</Text>}
                        </Box>
                        <FormHelperText fontSize="xs">Select one or more trucks for this driver.</FormHelperText>
                    </FormControl>
                </Grid>

                {/* --- Action Buttons --- */}
                <HStack spacing={4} justify="flex-end" pt={6}>
                    {onCancel && (
                        <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button 
                        type="submit" 
                        colorScheme="blue" 
                        isLoading={isSubmitting} 
                        loadingText={isEditMode ? 'Saving...' : 'Creating...'}
                        px={8}
                    >
                        {isEditMode ? 'Save Changes' : 'Create Driver'}
                    </Button>
                </HStack>
            </VStack>
        </Box>
    );
}